import { useCallback, useEffect, useMemo, useState } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import { add, format, parse, startOfWeek, getDay } from 'date-fns';
import { hu } from 'date-fns/locale';
import { rrulestr } from 'rrule';
import { useSelector } from 'react-redux';
import { backendHOST } from '../../../client-config/client-config';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Button, ButtonGroup, Typography, Box } from '@mui/material';
import { Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Grid,
  TextField,
} from '@mui/material';
import {
  AttendanceTypes,
  DiscountValues,
} from '../../../utils/enums/adminEnums';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Popover, IconButton, Divider } from '@mui/material';
const DragAndDropCalendar = withDragAndDrop(Calendar);
// --- date-fns localizer (Monday as first day of week) ---
const locales = { hu: hu };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: (date, _options) => startOfWeek(date, { weekStartsOn: 1 }),
  getDay,
  locales,
});

const headers = () => ({
  'Content-Type': 'application/json',
  'x-auth-token': localStorage.token,
});

function toEvent(e) {
  // Map backend schedule item -> RBC event
  return {
    id: e.scheduleId, // schedule id
    title: `${e.tanora_torz?.tanora_descr ?? ''} – ${
      e.oktato?.last_name ?? ''
    } ${e.oktato?.first_name ?? ''}`,
    start: new Date(e.date_start),
    end: new Date(e.date_to),
    resourceId: e.id_terem, // we’ll not group by resource, but keep it
    bgColor: e.oktato.color || undefined,
    raw: e,
  };
}

function expandRecurrence(items, rangeStart, rangeEnd) {
  const results = [];
  for (const e of items) {
    if (!e.recurrence) {
      results.push({
        ...e,
        seriesId: e.id,
        id: `${e.id}::${+new Date(e.start)}`,
      });
      continue;
    }
    try {
      const rule = rrulestr(e.recurrence);
      const between = rule.between(rangeStart, rangeEnd, true);
      for (const dt of between) {
        const durationMs = new Date(e.end) - new Date(e.start);
        results.push({
          ...e,
          seriesId: e.id, // az eredeti schedule id
          id: `${e.id}::${dt.getTime()}`, // EGYEDI példány ID a UI-nak
          start: dt,
          end: new Date(dt.getTime() + durationMs),
        });
      }
    } catch (_err) {
      results.push({ ...e, seriesId: e.id, id: `${e.id}::fallback` });
    }
  }
  return results;
}

function Toolbar({ date, label, onNavigate, onView, view }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 1,
      }}
    >
      <ButtonGroup variant='outlined' size='small'>
        <Button onClick={() => onNavigate('TODAY')}>Ma</Button>
        <Button onClick={() => onNavigate('PREV')}>Előző</Button>
        <Button onClick={() => onNavigate('NEXT')}>Következő</Button>
      </ButtonGroup>
      <Typography variant='h6' sx={{ mx: 2 }}>
        {
          format(date, 'LLLL dd – dd', {
            locale: hu,
          }) /* pl. november 03 – 09 */
        }
      </Typography>
      <ButtonGroup variant='outlined' size='small'>
        {/* <Button disabled={view === 'month'} onClick={() => onView('month')}>
          Havi
        </Button>*/}
        <Button disabled={view === 'week'} onClick={() => onView('week')}>
          Heti
        </Button>
        <Button disabled={view === 'day'} onClick={() => onView('day')}>
          Napi
        </Button>
      </ButtonGroup>
    </Box>
  );
}

function Header({ date }) {
  // “H 03”, “K 04”… (rövid magyar nap + nap)
  return (
    <Box sx={{ textAlign: 'center', py: 0.5, fontWeight: 600 }}>
      {format(date, 'EE dd', { locale: hu })}
    </Box>
  );
}

export default function TimetableRBC({ params, filters }) {
  const user = useSelector((s) => s.auth.user);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('week'); // was 'Heti'
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [ctxMenu, setCtxMenu] = useState(null);
  const [copyDlg, setCopyDlg] = useState({
    open: false,
    event: null,
    date: '', // 'YYYY-MM-DD'
    timeFrom: '', // 'HH:mm'
    timeTo: '', // 'HH:mm'
  });
  const [rooms, setRooms] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [selection, setSelection] = useState(null); // event being edited/viewed
  const [range, setRange] = useState({ start: null, end: null });

  // --- load lookup data (rooms, teachers) ---
  useEffect(() => {
    async function loadLookups() {
      try {
        const [r, t] = await Promise.all([
          fetch(`${backendHOST}/api/scriptantiaAdmin/Room`, {
            headers: headers(),
          }),
          fetch(`${backendHOST}/api/scriptantiaAdmin/Teacher`, {
            headers: headers(),
          }),
        ]);
        setRooms(await r.json());
        setTeachers(await t.json());
      } catch (e) {
        console.error(e);
      }
    }
    loadLookups();
  }, []);
  // tanár ID-t több lehetséges mezőből
  const getTeacherId = (ev) =>
    Number(
      ev?.raw?.id_oktato_real ?? ev?.raw?.id_oktato ?? ev?.raw?.oktato?.id ?? -1
    );

  // kliens oldali szűrés
  const applyFilters = useCallback(
    (list) => {
      let res = list;

      // Tanár szűrő
      if (filters?.teacherId != null && Number(filters.teacherId) >= 0) {
        const tid = Number(filters.teacherId);
        res = res.filter((e) => getTeacherId(e) === tid);
      }

      // „Csak aktívak”: ha van aktív jelzőnk, azt nézzük; törölt órákat is kivesszük
      if (filters?.onlyActive) {
        res = res.filter((e) => {
          const isDeleted = Number(e?.raw?.deleted ?? 0) === 1;
          const teacherActive =
            Number(e?.raw?.oktato?.active ?? e?.raw?.active ?? 1) === 1;
          return !isDeleted && teacherActive;
        });
      }

      return res;
    },
    [filters]
  );

  const roomResources = useMemo(
    () =>
      (rooms ?? []).map((r) => ({
        resourceId: r.id, // <- Ezzel egyezzen az event.resourceId
        resourceTitle: r.text, // Fejléc felirat
      })),
    [rooms]
  );

  // --- load schedules for visible range ---
  const loadSchedules = useCallback(
    async (centerDate) => {
      setLoading(true);
      try {
        const qDate = centerDate.toISOString().substring(0, 10);
        const res = await fetch(
          `${backendHOST}/api/scriptantiaAdmin/Schedule?date_filter=${qDate}`,
          { headers: headers() }
        );
        const data = await res.json();
        // Map to events
        const items = data.map(toEvent);
        const expanded = expandRecurrence(
          items,
          range.start ?? add(centerDate, { weeks: -1 }),
          range.end ?? add(centerDate, { weeks: 1 })
        );
        setEvents(expanded);
        setAllEvents(expanded);
        setEvents(applyFilters(expanded));
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    },
    [range.start, range.end, applyFilters]
  );
  useEffect(() => {
    setEvents(applyFilters(allEvents));
  }, [filters, allEvents, applyFilters]);

  useEffect(() => {
    loadSchedules(currentDate);
  }, [currentDate, loadSchedules]);

  // --- Drag & drop + resize handlers (update only) ---
  const canEdit = useCallback(
    (event) => {
      if (!user) return false;

      if (user.permissions?.includes('admin.all')) return true;
      // admin/jogosult bármit módosíthat
      if (event?.raw?.signed === 1) return false;

      if (user.permissions?.includes('timetable.details')) return true;
      // különben csak a saját óráit
      const teacherId = event?.raw?.id_oktato_real ?? event?.raw?.id_oktato;
      return teacherId === user?.ext_id_user;
    },
    [user]
  );

  const updateEventOnServer = async (evt, start, end) => {
    const raw = evt.raw || {};
    const tj = Array.isArray(raw.tanora_jelenlets) ? raw.tanora_jelenlets : [];
    const firstJelenletId = tj[0]?.jelenlet_id ?? evt.id; // bármi nem üres szám jó itt

    const payload = {
      // JSON branch feltétele
      jelenlet_id: firstJelenletId,
      // kötelező idő/dátum és azonosítók
      id_terem: raw.id_terem ?? evt.resourceId ?? null,
      id_oktato_real: raw.id_oktato_real ?? raw.id_oktato ?? null,
      date_start: start.toISOString(),
      date_to: end.toISOString(),
      datum: format(start, 'yyyy-MM-dd'),
      // a szerver végigiterál ezen a listán
      tanora_jelenlets: tj.map((j) => ({ jelenlet_id: j.jelenlet_id })),
      // map-ek (itt nem változtatunk jelenlét/discounton, üresen hagyjuk)
      jelenlet_real: {},
      discount_prc: {},
      send_notification: {},
      deletedx: {},
      // csak üzenethez használja
      tanora_torz: raw.tanora_torz ?? null,
    };
    await fetch(`${backendHOST}/api/scriptantiaAdmin/Schedule2`, {
      method: 'PUT',
      headers: { ...headers(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  };

  const onEventDrop = async ({ event, start, end }) => {
    if (!canEdit(event)) return;

    try {
      await updateEventOnServer(event, start, end);
      setEvents((prev) =>
        applyFilters(
          (allEvents.length ? allEvents : prev).map((e) =>
            e.id === event.id ? { ...e, start, end } : e
          )
        )
      );
      setAllEvents((prev) =>
        prev.map((e) => (e.id === event.id ? { ...e, start, end } : e))
      );
    } catch (e) {
      console.error(e);
    }
  };
  const onEventResize = async ({ event, start, end }) => {
    if (!canEdit(event)) return;
    try {
      await updateEventOnServer(event, start, end);
      setEvents((prev) =>
        applyFilters(
          (allEvents.length ? allEvents : prev).map((e) =>
            e.id === event.id ? { ...e, start, end } : e
          )
        )
      );
      setAllEvents((prev) =>
        prev.map((e) => (e.id === event.id ? { ...e, start, end } : e))
      );
    } catch (e) {
      console.error(e);
    }
  };

  // --- selection dialog (replacement for DevExtreme form) ---
  const openDialog = (evt) => {
    if (!canEdit(evt)) return;

    setSelection(evt);
  };
  const closeDialog = () => setSelection(null);

  // derive edit model
  const [editModel, setEditModel] = useState(null);
  useEffect(() => {
    if (!selection) {
      setEditModel(null);
      return;
    }
    const raw = selection.raw;
    const model = {
      id: raw.id,
      id_terem: raw.id_terem,
      id_oktato_real: raw.id_oktato_real,
      datum: raw.datum ?? format(selection.start, 'yyyy-MM-dd'),
      tanora_jelenlets: (raw.tanora_jelenlets ?? []).map((j) => ({
        ...j,
        schedule_attendance: j.schedule_attendance,
        discount_prc: j.discount_prc,
        deleted: j.deleted,
      })),
    };

    setEditModel(model);
  }, [selection]);

  const saveDialog = async () => {
    if (!editModel) return;
    const tj = Array.isArray(editModel.tanora_jelenlets)
      ? editModel.tanora_jelenlets
      : [];
    const jelenlet_real = {};
    const discount_prc = {};
    const deletedx = {};
    const send_notification = {}; // ha kell, most mindenkinél 0

    tj.forEach((j) => {
      // nálad a Radio 'A' / 'C' / 'M' kódot ad vissza -> ezt várja a backend
      if (j.schedule_attendance)
        jelenlet_real[j.jelenlet_id] = j.schedule_attendance;
      if (typeof j.discount_prc !== 'undefined')
        discount_prc[j.jelenlet_id] = j.discount_prc;
      deletedx[j.jelenlet_id] = j.deleted ? 1 : 0;
      send_notification[j.jelenlet_id] = j.send_notification ? 1 : 0;
    });
    const payload = {
      jelenlet_id: tj[0]?.jelenlet_id ?? editModel.id, // JSON branch miatt
      id_terem: editModel.id_terem,
      id_oktato_real: editModel.id_oktato_real,
      date_start: editModel.date_start,
      date_to: editModel.date_to,
      datum: editModel.datum,
      tanora_jelenlets: tj.map((j) => ({ jelenlet_id: j.jelenlet_id })),
      jelenlet_real,
      discount_prc,
      send_notification,
      deletedx,
      tanora_torz: editModel.tanora_torz ?? null,
    };
    try {
      await fetch(`${backendHOST}/api/scriptantiaAdmin/Schedule2`, {
        method: 'PUT',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      closeDialog();
      // refresh current day to reflect potential color/title changes
      loadSchedules(currentDate);
    } catch (e) {
      console.error(e);
    }
  };

  // --- event style: color by teacher if available ---
  const eventPropGetter = (event) => {
    const style = {};
    const editable = canEdit(event);

    if (event.raw.oktato.color) style.backgroundColor = event.raw.oktato.color;
    return {
      style,
      className: editable ? 'custom-event-no-label' : 'evt--locked',
    };
  };
  const openCopyDialog = (ev) => {
    const s = ev.start,
      e = ev.end;
    const toYMD = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
        d.getDate()
      ).padStart(2, '0')}`;
    const toHM = (d) =>
      `${String(d.getHours()).padStart(2, '0')}:${String(
        d.getMinutes()
      ).padStart(2, '0')}`;
    setCopyDlg({
      open: true,
      event: ev,
      date: toYMD(s),
      timeFrom: toHM(s),
      timeTo: toHM(e),
    });
  };
  const closeCopyDialog = () => setCopyDlg((x) => ({ ...x, open: false }));
  // --- custom renderer to mimic AppointmentInfos (icons + text) ---
  const EventComponent = ({ event }) => {
    const a = event.raw;
    const timeText = `${format(event.start, 'HH:mm')}–${format(
      event.end,
      'HH:mm'
    )}`;
    const studentNames =
      user?.permissions?.includes('timetable.details') ||
      a?.id_oktato_real === user?.ext_id_user
        ? (a?.tanora_jelenlets ?? [])
            .map((j) => j?.diak?.text)
            .filter(Boolean)
            .join(', ')
        : '';

    // --- telefonok óvatosan több lehetséges mezőből ---
    const teacherPhone = a?.oktato?.userProfile?.phone ?? '';
    const studentsWithPhone = (a?.tanora_jelenlets ?? [])
      .map((j) => ({
        name: j?.diak?.text ?? '',
        phone: j?.diak?.userProfile?.phone ?? '',
        phone2: j?.diak?.userProfile?.phone2 ?? '',
      }))
      .filter((x) => x.name);

    // Popover állapot
    const [anchorEl, setAnchorEl] = useState(null);
    const openInfo = (e) => {
      e.stopPropagation();
      setAnchorEl(e.currentTarget);
    };
    const closeInfo = (e) => {
      e?.stopPropagation?.();
      setAnchorEl(null);
    };
    const isOpen = Boolean(anchorEl);

    return (
      <Box
        sx={{ px: 0.1, lineHeight: 1.2 }}
        onContextMenu={(e) => openContextMenu(e, event)}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Box>
            <Typography
              variant='caption'
              sx={{ display: 'block', opacity: 0.9 }}
            >
              {a?.deleted === 1 && (
                <i className='fas fa-trash-alt' style={{ color: 'crimson' }} />
              )}
              {a?.signed === 1 && (
                <i
                  className='fas fa-file-signature'
                  style={{ color: 'seagreen' }}
                />
              )}
              {studentNames ? `${studentNames}` : ''}
            </Typography>
            <Typography
              variant='caption'
              component='span'
              sx={{ fontWeight: 600 }}
            >
              {a?.oktato?.last_name} {a?.oktato?.first_name}
            </Typography>
          </Box>
          {/* (i) ikon jobbra */}
          <IconButton
            size='large'
            aria-label='info'
            onMouseDown={(e) => e.stopPropagation()}
            onClick={openInfo}
            sx={{ p: 0.25, opacity: 0.9 }}
          >
            <InfoOutlinedIcon fontSize='inherit' />
          </IconButton>
        </Box>
        {a?.terem?.text && (
          <Typography variant='caption'>{a.terem.text}</Typography>
        )}
        {/* Telefon popover */}
        <Popover
          open={isOpen}
          anchorEl={anchorEl}
          onClose={closeInfo}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          disableRestoreFocus
        >
          <Box sx={{ p: 1.25, minWidth: 220 }}>
            <Typography
              variant='caption'
              sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}
            >
              Elérhetőségek
            </Typography>
            <Typography variant='caption' sx={{ display: 'block' }}>
              <strong>Oktató:</strong> {a?.oktato?.last_name}{' '}
              {a?.oktato?.first_name}
              {teacherPhone ? ` — ${teacherPhone}` : ''}
            </Typography>
            {studentsWithPhone.length > 0 && (
              <>
                <Divider sx={{ my: 0.75 }} />
                <Typography
                  variant='caption'
                  sx={{ fontWeight: 700, display: 'block', mb: 0.25 }}
                >
                  Diákok
                </Typography>
                {studentsWithPhone.map((s, idx) => (
                  <Typography
                    key={idx}
                    variant='caption'
                    sx={{ display: 'block' }}
                  >
                    {s.name}
                    {s.phone ? ` — ${s.phone}` : ''}
                    {s.phone2 ? `, Szülő: ${s.phone2}` : ''}
                  </Typography>
                ))}
              </>
            )}
          </Box>
        </Popover>
      </Box>
    );
  };

  // determine visible range to prefetch
  const onRangeChange = (r) => {
    // r is object or array depending on view
    if (Array.isArray(r)) {
      setRange({ start: r[0].start, end: r[r.length - 1].end });
    } else if (r?.start && r?.end) {
      setRange({ start: r.start, end: r.end });
    }
  };

  const openContextMenu = (mouseEvent, event) => {
    mouseEvent.preventDefault();
    mouseEvent.stopPropagation();
    setCtxMenu({
      mouseX: mouseEvent.clientX + 2,
      mouseY: mouseEvent.clientY - 6,
      event,
    });
  };

  const closeContextMenu = () => setCtxMenu(null);

  const submitCopy = async () => {
    const ev = copyDlg.event?.raw ?? {};
    // Forrás azonosító – ha nálad más a kulcs, cseréld (pl. ev.id vagy ev.scheduleId)
    const sourceScheduleId = ev.id ?? ev.scheduleId;

    const startLocal = `${copyDlg.date} ${copyDlg.timeFrom}`; // "2025-11-08 08:00"
    const endLocal = `${copyDlg.date} ${copyDlg.timeTo}`;

    const payload = {
      source_schedule_id: sourceScheduleId, // backend ebből másol mindent (diákok, terem, stb.)
      id_terem: ev.id_terem ?? copyDlg.event?.resourceId ?? null,
      id_oktato_real: ev.id_oktato_real ?? ev.id_oktato ?? null,
      datum: copyDlg.date,
      date_start: startLocal,
      date_to: endLocal,
    };

    try {
      await fetch(`${backendHOST}/api/scriptantiaAdmin/ScheduleCopy`, {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      closeCopyDialog();
      await loadSchedules(currentDate); // frissítés
    } catch (err) {
      console.error(err);
    }
  };
  return (
    <DndProvider backend={HTML5Backend}>
      <DragAndDropCalendar
        localizer={localizer}
        events={events}
        defaultView='week'
        view={view}
        resources={view === 'day' ? roomResources : undefined}
        resourceIdAccessor='resourceId'
        resourceTitleAccessor='resourceTitle'
        // (opcionális) így a napok és termek csoportosítása jól rendeződik:
        groupByDateAndResource
        onView={setView}
        date={currentDate}
        onNavigate={setCurrentDate}
        step={30}
        timeslots={2}
        popup
        onRangeChange={onRangeChange}
        onSelectEvent={openDialog}
        onEventDrop={onEventDrop}
        onEventResize={onEventResize}
        resizable
        selectable={false} // disable creating
        draggableAccessor={canEdit}
        resizableAccessor={canEdit}
        eventPropGetter={eventPropGetter}
        components={{
          event: EventComponent,
          toolbar: Toolbar,
          header: Header,
        }}
        formats={{
          dayFormat: (d, culture, l) => format(d, 'EE dd', { locale: hu }),
          timeGutterFormat: (d) => format(d, 'H:mm', { locale: hu }),
          eventTimeRangeFormat: ({ start, end }) => {
            return `${format(start, 'H:mm')}-${format(end, 'H:mm')}`;
          },
        }}
        culture='hu'
        messages={{
          week: 'Heti',
          day: 'Napi',
          month: 'Havi',
          today: 'Ma',
          previous: 'Előző',
          next: 'Következő',
          noEventsInRange: 'Nincs óra ebben az intervallumban',
        }}
        min={new Date(1970, 0, 1, 7, 0, 0)}
        max={new Date(1970, 0, 1, 22, 0, 0)}
        now={new Date()}
        style={{ height: '92vh', borderRadius: 12 }}
      />

      {/* Details Dialog */}
      <Dialog open={!!selection} onClose={closeDialog} maxWidth='md' fullWidth>
        <DialogTitle>Óra részletei</DialogTitle>
        <DialogContent dividers>
          {editModel ? (
            <Box sx={{ mt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size='small'>
                    <InputLabel>Terem</InputLabel>
                    <Select
                      label='Terem'
                      value={editModel.id_terem ?? ''}
                      onChange={(e) =>
                        setEditModel((m) => ({
                          ...m,
                          id_terem: e.target.value,
                        }))
                      }
                    >
                      {rooms.map((r) => (
                        <MenuItem value={r.id} key={r.id}>
                          {r.text}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size='small'>
                    <InputLabel>Oktató</InputLabel>
                    <Select
                      label='Oktató'
                      value={editModel.id_oktato_real ?? ''}
                      onChange={(e) =>
                        setEditModel((m) => ({
                          ...m,
                          id_oktato_real: e.target.value,
                        }))
                      }
                    >
                      {teachers.map((t) => (
                        <MenuItem value={t.id} key={t.id}>
                          {t.text}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size='small'
                    label='Dátum'
                    value={editModel.datum ?? ''}
                    type='date'
                    onChange={(e) =>
                      setEditModel((m) => ({ ...m, datum: e.target.value }))
                    }
                    placeholder='YYYY-MM-DD'
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 3 }}>
                <Typography variant='subtitle2' sx={{ mb: 1 }}>
                  Jelenlét és kedvezmény
                </Typography>
                {editModel.tanora_jelenlets?.map((j) => (
                  <Box
                    key={j.jelenlet_id}
                    sx={{
                      mb: 2,
                      p: 1,
                      border: '1px solid rgba(0,0,0,0.08)',
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant='subtitle2' sx={{ mb: 1 }}>
                      {j.diak?.text}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <FormControl>
                          <RadioGroup
                            row
                            value={j.schedule_attendance}
                            onChange={(e) => {
                              const val = e.target.value;
                              setEditModel((m) => ({
                                ...m,
                                tanora_jelenlets: m.tanora_jelenlets.map((x) =>
                                  x.jelenlet_id === j.jelenlet_id
                                    ? { ...x, schedule_attendance: val }
                                    : x
                                ),
                              }));
                            }}
                          >
                            {AttendanceTypes.map((a) => (
                              <FormControlLabel
                                key={a.key}
                                value={a.key}
                                control={<Radio />}
                                label={a.name}
                              />
                            ))}
                          </RadioGroup>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth size='small'>
                          <InputLabel>Kedvezmény</InputLabel>
                          <Select
                            label='Kedvezmény'
                            value={j.discount_prc ?? 0}
                            onChange={(e) => {
                              const val = e.target.value;
                              setEditModel((m) => ({
                                ...m,
                                tanora_jelenlets: m.tanora_jelenlets.map((x) =>
                                  x.jelenlet_id === j.jelenlet_id
                                    ? { ...x, discount_prc: val }
                                    : x
                                ),
                              }));
                            }}
                          >
                            {DiscountValues.map((d) => (
                              <MenuItem key={d.key} value={d.key}>
                                {d.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sm={2}
                        sx={{ display: 'flex', alignItems: 'center' }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={j.deleted === 1}
                              onChange={(e) => {
                                const val = e.target.checked ? 1 : 0;
                                setEditModel((m) => ({
                                  ...m,
                                  tanora_jelenlets: m.tanora_jelenlets.map(
                                    (x) =>
                                      x.jelenlet_id === j.jelenlet_id
                                        ? { ...x, deleted: val }
                                        : x
                                  ),
                                }));
                              }}
                            />
                          }
                          label='Óra törlése'
                        />
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Box>
            </Box>
          ) : (
            <Typography variant='body2'>Betöltés…</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Mégse</Button>
          <Button onClick={saveDialog} variant='contained'>
            Mentés
          </Button>
        </DialogActions>
      </Dialog>
      <Menu
        open={Boolean(ctxMenu)}
        onClose={closeContextMenu}
        anchorReference='anchorPosition'
        anchorPosition={
          ctxMenu ? { top: ctxMenu.mouseY, left: ctxMenu.mouseX } : undefined
        }
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ elevation: 4 }}
      >
        <MenuItem
          onClick={() => {
            openCopyDialog(ctxMenu?.event);
            closeContextMenu();
          }}
        >
          <ListItemIcon>
            <ContentCopyIcon fontSize='small' />
          </ListItemIcon>
          <ListItemText primary='Egyedi óra másolás' />
        </MenuItem>

        <MenuItem
          onClick={() => {
            closeContextMenu();
            openDialog(ctxMenu.event);
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize='small' />
          </ListItemIcon>
          <ListItemText primary='Szerkesztés' />
        </MenuItem>

        {/* opcionális:
  <MenuItem onClick={handleDelete}>
    <ListItemIcon><DeleteOutlineIcon fontSize="small" /></ListItemIcon>
    <ListItemText primary="Törlés" />
  </MenuItem>
  */}
      </Menu>
      <Dialog
        open={copyDlg.open}
        onClose={closeCopyDialog}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>Plusz óra másolás</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gap: 2, mt: 0.5 }}>
            <TextField
              label='Dátum'
              type='date'
              size='small'
              value={copyDlg.date}
              onChange={(e) =>
                setCopyDlg((x) => ({ ...x, date: e.target.value }))
              }
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label='Kezdés'
              type='time'
              size='small'
              value={copyDlg.timeFrom}
              onChange={(e) =>
                setCopyDlg((x) => ({ ...x, timeFrom: e.target.value }))
              }
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label='Befejezés'
              type='time'
              size='small'
              value={copyDlg.timeTo}
              onChange={(e) =>
                setCopyDlg((x) => ({ ...x, timeTo: e.target.value }))
              }
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCopyDialog}>Mégse</Button>
          <Button variant='contained' onClick={submitCopy}>
            Másolás
          </Button>
        </DialogActions>
      </Dialog>
    </DndProvider>
  );
}
